import { useCallback, useEffect, useState } from 'react';

import { useUser } from '@/utils/useUser';

interface UseFavoriteToolsReturn {
  favoriteTools: string[];
  isLoading: boolean;
  isFavorite: (slug: string) => boolean;
  addFavorite: (slug: string) => Promise<void>;
  removeFavorite: (slug: string) => Promise<void>;
  toggleFavorite: (slug: string) => Promise<void>;
}

// Global cache to prevent multiple fetches
// Cache is user-scoped to prevent cross-user data leaks
let cachedFavorites: { userId: string; favorites: string[] } | null = null;
let fetchPromise: Promise<string[]> | null = null;

export const useFavoriteTools = (): UseFavoriteToolsReturn => {
  const { user } = useUser();
  const userId = user?.id;
  const [favoriteTools, setFavoriteTools] = useState<string[]>(
    cachedFavorites && cachedFavorites.userId === userId
      ? cachedFavorites.favorites
      : [],
  );
  const [isLoading, setIsLoading] = useState(true);

  // Fetch favorites from database and handle migration
  useEffect(() => {
    if (!userId) {
      // Clear cache when user logs out to prevent cross-user data leaks
      cachedFavorites = null;
      fetchPromise = null;
      setFavoriteTools((current) => (current.length > 0 ? [] : current));
      setIsLoading(false);
      return;
    }

    // If we already have cached data for this user, use it immediately
    if (cachedFavorites !== null && cachedFavorites.userId === userId) {
      setFavoriteTools(cachedFavorites.favorites);
      setIsLoading(false);
      return;
    }

    // If cache exists but for a different user, invalidate it
    if (cachedFavorites !== null && cachedFavorites.userId !== userId) {
      cachedFavorites = null;
      fetchPromise = null;
    }

    // The session usually arrives after mount, when the no-user branch above
    // has already set isLoading to false: loading again until this user's
    // favorites are in (the /presets "Your tools" row waits on it).
    setIsLoading(true);

    // If a fetch is already in progress, wait for it
    if (fetchPromise) {
      fetchPromise
        .then((favorites) => {
          setFavoriteTools(favorites);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error from shared fetch:', error);
          setIsLoading(false);
        });
      return;
    }

    // Start a new fetch and cache the promise
    const fetchAndMigrate = async (): Promise<string[]> => {
      try {
        // Check if migration needed
        const localStorageFavorites = localStorage.getItem('favourites');

        if (localStorageFavorites) {
          // Migration needed - migrate then remove localStorage
          const parsedFavorites = JSON.parse(localStorageFavorites);

          const response = await fetch('/api/user-preferences', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'migrate',
              localStorageFavorites: parsedFavorites,
            }),
          });

          if (response.ok) {
            // Migration successful - remove localStorage key
            localStorage.removeItem('favourites');
          }
        }

        // Also check for old pinnedNavigation system
        const localStoragePinned = localStorage.getItem('pinnedNavigation');

        if (localStoragePinned) {
          // Migration needed - merge pinned items with favorites
          const parsedPinned = JSON.parse(localStoragePinned);

          const response = await fetch('/api/user-preferences', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'migrate',
              localStorageFavorites: parsedPinned,
            }),
          });

          if (response.ok) {
            // Migration successful - remove localStorage key
            localStorage.removeItem('pinnedNavigation');
          }
        }

        // Fetch current favorites from database
        const response = await fetch('/api/user-preferences');
        if (response.ok) {
          const data = await response.json();
          const favorites = data.favoriteTools || [];

          // Update global cache with user ID
          cachedFavorites = { userId, favorites };
          fetchPromise = null;

          return favorites;
        }

        throw new Error('Failed to fetch favorites');
      } catch (error) {
        console.error('Error fetching/migrating favorites:', error);

        // Fallback to localStorage if API fails
        const localFavorites = localStorage.getItem('favourites');
        if (localFavorites) {
          const favorites = JSON.parse(localFavorites);
          cachedFavorites = { userId, favorites };
          fetchPromise = null;
          return favorites;
        }

        fetchPromise = null;
        throw error;
      }
    };

    fetchPromise = fetchAndMigrate();

    fetchPromise
      .then((favorites) => {
        setFavoriteTools(favorites);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error in fetchAndMigrate:', error);
        setIsLoading(false);
      });
  }, [userId]);

  // Listen for favorites changes from other components
  useEffect(() => {
    const handleFavoritesChanged = (event: CustomEvent<string[]>) => {
      // Update state directly from event detail instead of fetching
      // Only update if user is logged in
      if (event.detail && userId) {
        setFavoriteTools(event.detail);
        // Update global cache with user ID
        cachedFavorites = { userId, favorites: event.detail };
      }
    };

    window.addEventListener(
      'favoriteToolsChanged',
      handleFavoritesChanged as EventListener,
    );
    return () => {
      window.removeEventListener(
        'favoriteToolsChanged',
        handleFavoritesChanged as EventListener,
      );
    };
  }, [userId]);

  const isFavorite = useCallback(
    (slug: string) => favoriteTools.includes(slug),
    [favoriteTools],
  );

  const addFavorite = useCallback(
    async (slug: string) => {
      // Optimistic update
      const newFavorites = favoriteTools.includes(slug)
        ? favoriteTools
        : [...favoriteTools, slug];
      setFavoriteTools(newFavorites);

      try {
        const response = await fetch('/api/user-preferences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'add', toolSlug: slug }),
        });

        if (!response.ok) {
          // Rollback on error
          setFavoriteTools(favoriteTools);
        } else {
          // Notify other components with the updated favorites array
          window.dispatchEvent(
            new CustomEvent('favoriteToolsChanged', { detail: newFavorites }),
          );
        }
      } catch (error) {
        console.error('Error adding favorite:', error);
        // Rollback on error
        setFavoriteTools(favoriteTools);
      }
    },
    [favoriteTools],
  );

  const removeFavorite = useCallback(
    async (slug: string) => {
      // Optimistic update
      const newFavorites = favoriteTools.filter((s) => s !== slug);
      setFavoriteTools(newFavorites);

      try {
        const response = await fetch('/api/user-preferences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'remove', toolSlug: slug }),
        });

        if (!response.ok) {
          // Rollback on error
          setFavoriteTools(favoriteTools);
        } else {
          // Notify other components with the updated favorites array
          window.dispatchEvent(
            new CustomEvent('favoriteToolsChanged', { detail: newFavorites }),
          );
        }
      } catch (error) {
        console.error('Error removing favorite:', error);
        // Rollback on error
        setFavoriteTools(favoriteTools);
      }
    },
    [favoriteTools],
  );

  const toggleFavorite = useCallback(
    async (slug: string) => {
      if (favoriteTools.includes(slug)) {
        await removeFavorite(slug);
      } else {
        await addFavorite(slug);
      }
    },
    [favoriteTools, addFavorite, removeFavorite],
  );

  return {
    favoriteTools,
    isLoading,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
  };
};
